import { useCallback, useEffect, useMemo, useState } from 'react';

import { useSelector } from 'react-redux';

import { hasOrgWideAccess, resolveOrgAccess, useFirestoreQueryBatched } from '@src/packages/utils';
import useGetOrgLocationGroups from '@src/packages/utils/hooks/collections/useGetOrgLocationGroups';
import LocationRepository from '@src/repositories/LocationRepository';

import type {
  LocationCombinerData,
  LocationCombinerIsAGroup,
  LocationCombinerValue,
  TriggerLocation,
  TriggerLocationGroup,
} from '@src/models/interfaces/trigger.interface';

import type {
  LocationTypeFilter,
  UseLocationCombinerParams,
  UseLocationCombinerReturn,
} from './LocationCombiner.types';

interface LocationCombinerAccessState {
  user?: {
    access?: { data?: { claims?: Parameters<typeof resolveOrgAccess>[0] } };
    orgUser?: { data?: { access?: string } };
  };
}

const INIT_DATA: LocationCombinerValue = {
  isAGroup: -1,
  data: null,
};

/**
 * A location can only be picked once per `type`: once a location of a given type is
 * selected, the remaining (unselected) locations of that same type become unavailable.
 */
const isLocationTypeAlreadySelected = (
  loc: TriggerLocation,
  selectedIds: string[],
  allLocations: TriggerLocation[],
): boolean => {
  if (selectedIds.includes(loc.id)) {
    return false;
  }

  const selectedLocations = allLocations.filter(item => selectedIds.includes(item.id));
  return !!selectedLocations.find(selected => selected.type === loc.type);
};

/**
 * Smart layer for {@link LocationCombiner}: owns the selection state, fetches the
 * division/org locations and location groups through the repository layer, and exposes
 * ready-to-render data plus change handlers. The component stays presentational.
 */
export const useLocationCombiner = ({
  divisionId = null,
  divisionIds = null,
  value = null,
  onChange,
  disabled = false,
}: UseLocationCombinerParams): UseLocationCombinerReturn => {
  const orgId = useSelector(
    ({ organizations }: { organizations: { organization: { id: string } } }) =>
      organizations.organization.id,
  );
  const claims = useSelector(
    (state: LocationCombinerAccessState) => state.user?.access?.data?.claims,
  );
  const orgUserAccess = useSelector(
    (state: LocationCombinerAccessState) => state.user?.orgUser?.data?.access,
  );

  // `organizations/{orgId}/locations` is readable by organization roles only (firestore.rules).
  // A division-scoped user querying it gets PERMISSION_DENIED, so the query is not even issued:
  // those locations are not theirs to see, and asking for them only produced a failed request.
  const canReadOrgLocations = hasOrgWideAccess(resolveOrgAccess(claims, orgId, orgUserAccess));

  const repository = useMemo(() => new LocationRepository(), []);

  const [location, setLocation] = useState<LocationCombinerValue>(INIT_DATA);
  const [isHelpVisible, setIsHelpVisible] = useState(false);
  const [isGroupFormOpen, setIsGroupFormOpen] = useState(false);
  const [isTypeFilterOpen, setIsTypeFilterOpen] = useState(false);
  const [isGroupPickerOpen, setIsGroupPickerOpen] = useState(false);
  const [groupSearch, setGroupSearch] = useState('');
  const [markerSearch, setMarkerSearch] = useState('');
  const [markerTypeFilter, setMarkerTypeFilter] = useState<LocationTypeFilter>('all');
  const [markerTableElement, setMarkerTableElement] = useState<HTMLDivElement | null>(null);
  const [markerTablePageSize, setMarkerTablePageSize] = useState(5);

  const divisionIdList = (divisionIds ?? (divisionId != null ? [divisionId] : [])).filter(Boolean);
  const primaryDivisionId = divisionIdList[0] ?? null;
  const divisionIdsKey = divisionIdList.join(',');
  const isLocationsMode = location.isAGroup === false;

  useEffect(() => {
    if (!markerTableElement || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const updatePageSize = (height: number) => {
      const fixedTableHeight = 112;
      const rowHeight = 56;
      const nextPageSize = Math.max(5, Math.floor((height - fixedTableHeight) / rowHeight));

      setMarkerTablePageSize(current => (current === nextPageSize ? current : nextPageSize));
    };
    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) {
        updatePageSize(entry.contentRect.height);
      }
    });

    observer.observe(markerTableElement);
    updatePageSize(markerTableElement.clientHeight);

    return () => observer.disconnect();
  }, [markerTableElement]);

  const divGroupQueries = orgId
    ? divisionIdList.map(divId => repository.getLocationGroupsByDivisionId(orgId, divId))
    : [];

  const { data: divGroupLocations = [], loading: divGroupLocationsLoading } =
    useFirestoreQueryBatched(divGroupQueries, [orgId, divisionIdsKey]);

  const { data: orgGroupLocations = [], loading: orgGroupLocationsLoading } =
    useGetOrgLocationGroups([isGroupFormOpen]);

  const divLocationQueries =
    orgId && isLocationsMode
      ? divisionIdList.map(divId => repository.getLocationsByDivisionId(orgId, divId))
      : [];

  const orgLocationQueries =
    orgId && isLocationsMode && canReadOrgLocations ? [repository.getLocationsByOrgId(orgId)] : [];

  const locationQueries = [...divLocationQueries, ...orgLocationQueries];

  const { data: allLocations = [], loading: locationsLoading } = useFirestoreQueryBatched(
    locationQueries,
    [orgId, divisionIdsKey, isLocationsMode, canReadOrgLocations],
  );

  const groupsLoading = divGroupLocationsLoading || orgGroupLocationsLoading;
  const mainLoading = groupsLoading || (isLocationsMode && locationsLoading);

  const locationGroupsMerged: TriggerLocationGroup[] = useMemo(
    () => [...divGroupLocations, ...orgGroupLocations],
    [divGroupLocations, orgGroupLocations],
  );

  const selectedLocationIds = useMemo(() => {
    if (!Array.isArray(location?.data)) {
      return [];
    }

    return location.data.map(loc => loc.id);
  }, [location?.data]);

  const onChangeMode = useCallback(
    (isAGroup: LocationCombinerIsAGroup) => {
      const output: LocationCombinerValue = { data: null, isAGroup };
      setLocation(output);
      onChange(output);
    },
    [onChange],
  );

  const onChangeLocationGroups = useCallback(
    (groupId: string) => {
      const newLoc = locationGroupsMerged.find(item => item.id === groupId);
      const output: LocationCombinerValue = {
        isAGroup: true,
        data: (newLoc ?? null) as LocationCombinerData,
      };
      setLocation(output);
      onChange(output);
    },
    [locationGroupsMerged, onChange],
  );

  const onChangeLocations = useCallback(
    (selectedIds: string[]) => {
      const output: LocationCombinerValue = {
        isAGroup: false,
        data: (allLocations as TriggerLocation[]).filter(item => selectedIds.includes(item.id)),
      };
      setLocation(output);
      onChange(output);
    },
    [allLocations, onChange],
  );

  const isLocationOptionDisabled = useCallback(
    (loc: TriggerLocation) =>
      isLocationTypeAlreadySelected(loc, selectedLocationIds, allLocations as TriggerLocation[]),
    [selectedLocationIds, allLocations],
  );

  const openGroupPicker = useCallback(() => {
    setGroupSearch('');
    setIsGroupPickerOpen(true);
  }, []);
  const closeGroupPicker = useCallback(() => setIsGroupPickerOpen(false), []);

  const openHelp = useCallback(() => setIsHelpVisible(true), []);
  const closeHelp = useCallback(() => setIsHelpVisible(false), []);
  const openTypeFilter = useCallback(() => setIsTypeFilterOpen(true), []);
  const closeTypeFilter = useCallback(() => setIsTypeFilterOpen(false), []);
  const openGroupForm = useCallback(() => setIsGroupFormOpen(true), []);
  const closeGroupForm = useCallback(() => setIsGroupFormOpen(false), []);

  useEffect(() => {
    if (value) {
      setLocation(prev => ({
        ...prev,
        data: value?.data,
        isAGroup: value?.isAGroup,
      }));
    }
  }, [value]);

  const selectedGroupId = (location?.data as TriggerLocationGroup | null)?.id;

  return {
    mode: location.isAGroup,
    primaryDivisionId,
    locationGroupsMerged,
    allLocations: allLocations as TriggerLocation[],
    selectedLocationIds,
    selectedGroupId,
    isHelpVisible,
    isGroupFormOpen,
    isTypeFilterOpen,
    isGroupPickerOpen,
    groupSearch,
    setGroupSearch,
    openGroupPicker,
    closeGroupPicker,
    markerSearch,
    markerTypeFilter,
    markerTablePageSize,
    radioDisabled: groupsLoading || disabled,
    groupSelectDisabled: !locationGroupsMerged.length || groupsLoading || disabled,
    locationsSelectDisabled: !allLocations.length || mainLoading || disabled,
    mainLoading,
    onChangeMode,
    onChangeLocationGroups,
    onChangeLocations,
    setMarkerSearch,
    setMarkerTypeFilter,
    setMarkerTableElement,
    isLocationOptionDisabled,
    openHelp,
    closeHelp,
    openTypeFilter,
    closeTypeFilter,
    openGroupForm,
    closeGroupForm,
  };
};

export default useLocationCombiner;
